import clsx from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";

import styles from "./table.module.css";

const DEFAULT_PAGE_SIZES = [5, 10, 20] as const;

export type TablePaginationProps = {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
  className?: string;
};

export function TablePagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  className,
}: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const canPrev = safePage > 1;
  const canNext = safePage < totalPages;

  return (
    <div className={clsx(styles.pagination, className)} role="navigation" aria-label="Table pagination">
      <span className={styles.paginationMeta}>
        Page {safePage} of {totalPages}
      </span>
      <div className={styles.pageNav}>
        <button
          type="button"
          className={styles.pageButton}
          disabled={!canPrev}
          onClick={() => onPageChange(safePage - 1)}
        >
          <ChevronLeft size={16} strokeWidth={1.75} aria-hidden="true" /> Previous
        </button>
        <button
          type="button"
          className={styles.pageButton}
          disabled={!canNext}
          onClick={() => onPageChange(safePage + 1)}
        >
          Next <ChevronRight size={16} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </div>
      {onPageSizeChange ? (
        <label className={styles.pageSize}>
          <span>Rows per page</span>
          <select
            className={styles.pageSizeSelect}
            value={String(pageSize)}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}
